import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, MapPin, Phone, Linkedin, Twitter, Github, Youtube, Send, ShieldCheck, Zap, Globe } from 'lucide-react'
import { Button } from '@/components/ui/Button'

const footerLinks = {
  categories: [
    { name: 'Core Matrix', href: '/category/programming' },
    { name: 'Web Protocols', href: '/category/web-development' },
    { name: 'Neural Nets', href: '/category/ai' },
    { name: 'Data Streams', href: '/category/data' },
    { name: 'Security Shields', href: '/category/security' },
  ],
  company: [
    { name: 'The Agency', href: '/about' },
    { name: 'Uplink', href: '/contact' },
    { name: 'Core Services', href: 'https://apexsofteck.com/services' },
    { name: 'Operations', href: 'https://apexsofteck.com/portfolio' },
    { name: 'Main Corporate Site', href: 'https://apexsofteck.com' },
  ],
  legal: [
    { name: 'Privacy Protocol', href: '/privacy' },
    { name: 'Terms of Engagement', href: '/terms' },
    { name: 'Data Cookies', href: '/cookies' },
  ],
}

const socialLinks = [
  { icon: Linkedin, href: 'https://linkedin.com/company/apexsofteck', label: 'LinkedIn', color: 'hover:text-[#0077B5]' },
  { icon: Twitter, href: 'https://twitter.com/apexsofteck', label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
  { icon: Github, href: 'https://github.com/apexsofteck', label: 'GitHub', color: 'hover:text-white' },
  { icon: Youtube, href: 'https://youtube.com/apexsofteck', label: 'YouTube', color: 'hover:text-[#FF0000]' },
]

export function Footer() {
  return (
    <motion.footer 
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 1 }}
      viewport={{ once: true }}
      className="relative bg-[#020813] border-t border-cyan-500/10 pt-24 pb-12 overflow-hidden"
    >
      {/* Background Ambience */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent shadow-[0_0_20px_rgba(6,182,212,0.5)]" />
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-cyan-500/5 blur-[120px] rounded-full" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-16 mb-20">
          {/* Brand & Mission */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block group mb-8">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute -inset-2 bg-cyan-500/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img 
                    src="/Apex Software Technologies.png" 
                    alt="ApexSofteck" 
                    className="h-12 w-auto relative object-contain"
                  />
                </div>
                <div className="h-8 w-px bg-white/10 hidden sm:block" />
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.3em] hidden sm:block">Matrix Blog</span>
              </div>
            </Link>
            <p className="text-white/40 text-sm leading-relaxed mb-8 max-w-xs font-medium">
              Architecting the digital frontier. Delivering precise technical intelligence across global neural networks.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-xl bg-white/5 border border-white/5 flex items-center justify-center text-white/20 transition-all duration-300 hover:scale-110 hover:bg-cyan-500/10 hover:border-cyan-500/30 ${social.color}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Links Matrix */}
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-8">
             <div>
              <h3 className="text-white font-black text-[10px] uppercase tracking-widest mb-8 flex items-center gap-2">
                <Zap className="w-3 h-3 text-cyan-400" />
                Sectors
              </h3>
              <ul className="space-y-4">
                {footerLinks.categories.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="text-white/40 hover:text-cyan-400 transition-colors text-xs font-bold uppercase tracking-wider">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-black text-[10px] uppercase tracking-widest mb-8 flex items-center gap-2">
                <Globe className="w-3 h-3 text-cyan-400" />
                Network
              </h3>
              <ul className="space-y-4">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="text-white/40 hover:text-cyan-400 transition-colors text-xs font-bold uppercase tracking-wider">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-span-2 md:col-span-1">
              <h3 className="text-white font-black text-[10px] uppercase tracking-widest mb-8 flex items-center gap-2">
                <MapPin className="w-3 h-3 text-cyan-400" />
                Node Uplink
              </h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-cyan-400/40" />
                  <a href="mailto:info@apexsofteck.com" className="text-white/40 hover:text-white text-xs font-mono">
                    info@apexsofteck.com
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-cyan-400/40" />
                  <span className="text-white/40 text-xs font-mono">+1 Protocol</span>
                </li>
                <li className="flex items-center gap-3">
                  <ShieldCheck className="w-4 h-4 text-green-500/40" />
                  <span className="text-white/40 text-[10px] font-black uppercase tracking-widest">USA_SECURE_NODE</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Stay Synced - Newsletter */}
          <div className="lg:col-span-1">
            <div className="p-8 backdrop-blur-xl bg-white/5 border border-cyan-500/10 rounded-[2rem] relative group hover:border-cyan-500/30 transition-all">
               <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <Send className="w-12 h-12 text-cyan-400" />
               </div>
               <h3 className="text-xl font-bold text-white mb-2">Stay Synced</h3>
               <p className="text-white/40 text-xs mb-6 leading-relaxed">
                  Inject the latest technical intelligence into your neural stream.
               </p>
               <div className="space-y-3">
                  <div className="relative">
                    <input 
                      type="email" 
                      placeholder="Uplink ID (Email)" 
                      className="w-full bg-[#0a1222] border border-cyan-500/20 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/10 focus:outline-none focus:border-cyan-500/50 transition-all"
                    />
                  </div>
                  <Button className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black font-black text-xs uppercase tracking-widest shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all">
                    Initialize Protocol
                  </Button>
               </div>
            </div>
          </div>
        </div>

        {/* Binary Bottom */}
        <div className="border-t border-cyan-500/5 pt-12 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-white/20 text-[10px] font-mono tracking-widest uppercase">
              Authenticated: {new Date().getFullYear()} © ApexSofteck Matrix
            </p>
            <span className="text-[10px] text-cyan-500/20 font-mono">GEN_REPLICA_VER_3.0.4</span>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8">
            {footerLinks.legal.map((link) => (
              <Link 
                key={link.name} 
                to={link.href} 
                className="text-white/20 hover:text-cyan-400 text-[10px] font-black uppercase tracking-widest transition-all"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4 px-4 py-2 bg-white/5 border border-cyan-500/10 rounded-full">
             <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
             <span className="text-[9px] font-black text-cyan-400 uppercase tracking-[0.2em]">Global Sync Stable</span>
          </div>
        </div>
      </div>
    </motion.footer>
  )
}
